import React from 'react';
import { useTranslation } from 'react-i18next';

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.about')}</span>
      </nav>

      {/* Page Header */}
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('about.title')}</h1>
        <p className="text-lg text-stone-600 max-w-4xl leading-relaxed">{t('about.description')}</p>
      </div>

      {/* About Content */}
      <div className="grid lg:grid-cols-3 gap-8 mb-16">
        <div className="lg:col-span-2 space-y-6">
          <p className="text-stone-700 leading-relaxed">{t('about.description1')}</p>
          <p className="text-stone-700 leading-relaxed">{t('about.description2')}</p>

          {/* Key Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">21</div>
              <div className="text-sm text-stone-600 mt-1">{t('hero.udas')}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">4</div>
              <div className="text-sm text-stone-600 mt-1">{t('hero.regionalOffices')}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">26</div>
              <div className="text-sm text-stone-600 mt-1">{t('hero.districts')}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">Online</div>
              <div className="text-sm text-stone-600 mt-1">{t('hero.onlineServices')}</div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="bg-stone-50 rounded-lg p-6 border border-stone-200">
            <div className="flex items-center space-x-3 mb-4">
              <img src="/images/header/ap-logo-large.png" alt="AP Government" className="w-16 h-16 object-contain" />
              <div>
                <h3 className="font-bold text-stone-800">{t('about.govtOfAP')}</h3>
                <p className="text-sm text-stone-600">{t('about.planningAuthority')}</p>
              </div>
            </div>
            <div className="border-t border-stone-200 pt-4 mt-4 space-y-3">
              <div className="flex items-center space-x-2 text-sm text-stone-600">
                <i className="ri-map-pin-line text-amber-600"></i>
                <span>Mangalagiri, Guntur District, AP</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-stone-600">
                <i className="ri-phone-line text-amber-600"></i>
                <span>0863-2000662</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-stone-600">
                <i className="ri-mail-line text-amber-600"></i>
                <span>dtcp-ap@gov.in</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Functions */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-stone-800 mb-8 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          Key Functions
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-stone-200 rounded-lg p-6 hover:shadow-md hover:border-amber-300 transition-all">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-map-2-line w-6 h-6 flex items-center justify-center text-amber-600"></i>
            </div>
            <h3 className="font-bold text-stone-800 mb-2">{t('about.urbanPlanning')}</h3>
            <p className="text-sm text-stone-600 leading-relaxed">{t('about.urbanPlanningDesc')}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-6 hover:shadow-md hover:border-amber-300 transition-all">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-building-2-line w-6 h-6 flex items-center justify-center text-amber-600"></i>
            </div>
            <h3 className="font-bold text-stone-800 mb-2">{t('about.buildingRegulation')}</h3>
            <p className="text-sm text-stone-600 leading-relaxed">{t('about.buildingRegulationDesc')}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-6 hover:shadow-md hover:border-amber-300 transition-all">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-layout-grid-line w-6 h-6 flex items-center justify-center text-amber-600"></i>
            </div>
            <h3 className="font-bold text-stone-800 mb-2">{t('about.layoutApprovals')}</h3>
            <p className="text-sm text-stone-600 leading-relaxed">{t('about.layoutApprovalsDesc')}</p>
          </div>
        </div>
      </div>

      {/* Director Info */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-8 border border-amber-200">
        <h2 className="text-2xl font-bold text-stone-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          Leadership
        </h2>
        <div className="flex items-center space-x-6">
          <div className="w-20 h-20 bg-amber-200 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-user-star-line w-10 h-10 flex items-center justify-center text-amber-700"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-stone-800">{t('header.directorName')}</h3>
            <p className="text-amber-700 font-medium">{t('header.directorTitle')}</p>
            <p className="text-sm text-stone-600 mt-2">
              Directorate of Town & Country Planning, Government of Andhra Pradesh
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
